<template>
    <div class="relative mb-4 flex flex-col gap-2">
        <vx-box v-if="isCustomAttributeKeyValue"
            class="relative flex p-4 gap-2 items-center flex-wrap lg:flex-nowrap lg:py-3 lg:pl-6 lg:pr-3">
            <div class="w-24 shrink-0">
                <and-if></and-if>
            </div>
            <div class="grow w-full flex flex-col gap-2 order-2 lg:order-1 lg:grow-0">
                <attribute-select :modelValue="filter.attribute_name"
                    :error="displayFirstError(validate.attribute_name)"
                    @update:modelValue="selectAttribute">
                </attribute-select>
            </div>
            <remove-filter class="ml-auto order-1 lg:order-2"></remove-filter>
        </vx-box>

        <vx-box class="relative flex flex-wrap items-start gap-2 lg:flex-nowrap"
            :class="{
                'p-4 ml-10 lg:p-6 lg:ml-32': isCustomAttributeKeyValue,
                'p-4 lg:py-3 lg:pl-6 lg:pr-3': !isCustomAttributeKeyValue,
            }">
            <div v-if="!isCustomAttributeKeyValue" class="w-24 pt-2">
                <and-if></and-if>
            </div>
            <div class="flex flex-col gap-2 grow w-full order-2 lg:order-1 lg:w-auto">
                <div v-if="!isCustomAttributeKeyValue">
                    <attribute-select :modelValue="filter.attribute_name"
                        :error="displayFirstError(validate.attribute_name)"
                        @update:modelValue="selectAttribute">
                    </attribute-select>
                </div>
                <div v-else
                    class="relative text-base font-extrabold text-slate-800">
                    Custom Attribute
                    <div
                        class="absolute inset-bs-1/2 -translate-y-1/2 h-px border-solid border-y border-x-0 border-t-slate-300 border-b-white -inset-s-[40px] w-[23px] lg:-inset-s-28 lg:w-[86px]">
                    </div>
                </div>

                <div v-if="isCustomAttributeKeyValue">
                    <vx-selectable
                        placeholder="key"
                        data-test="custom-attribute"
                        v-model="filter.key"
                        :options="autocompleteItems"
                        @search="fetchDropdownData"
                        taggable
                        :error="displayFirstError(validate.key)"
                        :clearable="false" />
                </div>

                <!-- v-show is used here instead of v-if because we rely on the watch state of DataTypeSelect.
                    External list filters must keep data_type null, so DataTypeSelect cannot mount and assign its default. -->
                <div v-if="!isExternalContactList" v-show="dataTypesLength > 1">
                    <data-type-select :modelValue="filter.data_type"
                        @update:modelValue="selectDataType"
                        :filter="filter"
                        :error="displayFirstError(validate.data_type)"
                        @lengthChanged="dataTypesLength = $event"></data-type-select>
                </div>

                <div :class="[operatorCol]">
                    <operator-select
                        :modelValue="filter.operator"
                        :disabled="!filter.attribute_name"
                        :error="displayFirstError(validate.operator)"
                        @update:modelValue="selectOperator"
                        :filter="filter"></operator-select>
                </div>

                <div v-if="isExternalContactList">
                    <external-list-select
                        :modelValue="selectedExternalContactList"
                        :notFound="selectedExternalContactListNotFound"
                        :error="displayFirstError(validate.value)"
                        data-test="external-contact-list-select"
                        @update:modelValue="selectExternalContactList">
                    </external-list-select>
                </div>

                <div v-if="hasValue && !isExternalContactList">
                    <vx-selectable v-if="isApiCall && filter.attribute_name !== 'contacts.custom_attributes.key_value' && filter.attribute_name !== 'contacts.custom_attributes.exclude_key_value'"
                        placeholder="value"
                        v-model="filter.value"
                        :disabled="!filter.attribute_name"
                        :options="autocompleteItems"
                        @search="fetchDropdownData"
                        taggable
                        data-test="value-select"
                        :error="displayFirstError(validate.value)"
                        :clearable="false" />
                    <vx-selectable v-else-if="options.length"
                        placeholder="value"
                        v-model="filter.value"
                        :disabled="!filter.attribute_name"
                        label="label"
                        :reduce="option => option.value"
                        :options="options"
                        data-test="value-select"
                        :error="displayFirstError(validate.value)"
                        :clearable="false"></vx-selectable>
                    <date-picker-input v-else-if="!shouldShowPositiveInt && filter.data_type === 'date'"
                        v-model="filter.value"
                        :disabled="!filter.attribute_name"
                        placeholder="MM/DD/YYYY"
                        format="MM/DD/YYYY"
                        :library="true"
                        :error="displayFirstError(validate.value)"
                        data-test="value-date-picker" />
                    <vx-input v-else-if="shouldShowPositiveInt"
                        v-model="filter.value"
                        :disabled="!filter.attribute_name"
                        type="number"
                        :min="1"
                        :max="2000"
                        :step="1"
                        placeholder="value"
                        :error="displayFirstError(validate.value)"
                        data-test="value-positive-int" />
                    <vx-input v-else
                        v-model="filter.value"
                        :disabled="!filter.attribute_name"
                        type="text"
                        placeholder="value"
                        :error="displayFirstError(validate.value)"
                        data-test="value-input" />
                </div>
                <div v-if="canIgnoreYear" class="bg-slate-50 text-slate-800 flex items-center justify-between p-4 rounded-2xl">
                    <div>Should we <strong>ignore the year?</strong></div>
                    <vx-toggle size="sm" :modelValue="ignoringYear" @update:modelValue="toggleIgnoringYear($event)"></vx-toggle>
                </div>
                <div v-if="showProfile" data-test="profile-selection">
                    <div class="pb-2 text-slate-800">In <strong>Profile</strong></div>
                    <vx-radio-selection :modelValue="filter.profile ?? 'corporate'"
                        @update:modelValue="selectProfile">
                        <vx-radio-selection-item value="corporate">
                            <template v-slot:header>Corporate</template>
                        </vx-radio-selection-item>
                        <vx-radio-selection-item value="local">
                            <template v-slot:header>Local</template>
                            <template v-slot:content>{{ props.localProfileDescription }}</template>
                        </vx-radio-selection-item>
                    </vx-radio-selection>
                </div>
            </div>

            <remove-filter v-if="!isCustomAttributeKeyValue" class="ml-auto order-1 lg:ml-0 lg:order-2"></remove-filter>

        </vx-box>
    </div>
</template>

<script setup>
import { h, ref, computed, inject, onMounted, nextTick } from 'vue'
import { customAttributesClient, externalListsClient, tagsClient } from '~/services';
import DatePickerInput from '../../../../general/DatePickerInput.vue';
import ExternalListSelect from '../../../../general/external-contact-lists/ExternalListSelect.vue';
import { entryPointsForSelect } from '~/components/filters';
import { phoneStateOptions } from '~/constants/phoneStateOptions';
import { useToasted } from '../../../../../composables/useToasted';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faShareNodes, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { VxButton, VxBox, VxInput, VxRadioSelection, VxRadioSelectionItem, VxSelectable, VxToggle } from '@voxie/frontend-components';
import AttributeSelect from './AttributeSelect.vue';
import OperatorSelect from './OperatorSelect.vue';
import DataTypeSelect from './DataTypeSelect.vue';
import { OPERATORS_DATE, OPERATORS_N, OPERATORS_THAT_DONT_NEED_VALUE, PROFILE_ATTRIBUTES } from './segmentFiltersHelpers';
import debounce from '~/utils/debounce';
import useVuelidate from '@vuelidate/core';
import { between, helpers, maxLength, numeric, required, requiredIf } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';
import dayjs from 'dayjs';

const FEATURES = inject('FEATURES');

const filter = defineModel({type: Object, required: true});

const props = defineProps({
    isFirst: {
        type: Boolean,
        default: false,
    },
    localProfileDescription: String,
});

const emit = defineEmits(['removeFilter'])

const rules = computed(() => {
    const defaultRules = {
        attribute_name: {
            required: helpers.withMessage('Attribute is required', required),
        },
        key: {
            requiredIf: helpers.withMessage('Key is required', requiredIf(() => isCustomAttributeKeyValue.value)),
            maxLength: maxLength(191),
        },
        data_type: {
            requiredIf: helpers.withMessage('Data Type is required', requiredIf(() => isCustomAttributeKeyValue.value)),
        },
        operator: {
            required: helpers.withMessage('Operator is required', required),
        },
        value: {
            requiredIf: helpers.withMessage('Value is required', requiredIf(() => hasValue.value)),
            regex: helpers.withMessage('Segment value is invalid', helpers.regex(/^[ 0-9a-zA-Z$%&’'()+,\-!#?|./:<@_]*$/)),
            maxLength: maxLength(60),
        },
    }

    if (filter.value.data_type === 'number') {
        defaultRules.value = {
            ...defaultRules.value,
            numeric
        }
    }

    if (filter.value.data_type === 'date') {
        if (OPERATORS_N.includes(filter.value.operator)) {
            const max = filter.value.operator.includes('month') ? 64 : 2000;
            defaultRules.value.between = between(1, max);
        }

        if (['=', '!=', '<', '<=', '>', '>='].includes(filter.value.operator)) {
            defaultRules.value.validDate = helpers.withMessage('Segment value is invalid', () => {
                return dayjs(filter.value.value, 'YYYY-MM-DD', true).isValid();
            });
        }
    }

    return defaultRules;
})

const validate = useVuelidate(rules, filter);

const AndIf = () => {
    return h('div', {}, [
        h(
            FontAwesomeIcon,
            { class: 'mr-2 text-sky-500', icon: faShareNodes }
        ),
        h(
            'span',
            { class: 'whitespace-nowrap text-slate-800 text-base font-extrabold' },
            [props.isFirst ? 'If' : 'And If']
        ),
    ]);
}

const RemoveFilter = (props) => {
    return h(VxButton, {
        class: `w-12 ${props.class}`,
        onClick: () => emit('removeFilter'),
        type: 'button',
        size: 'md',
        color: 'muted-light',
        'data-test': 'remove-filter',
    }, () => [
        h(FontAwesomeIcon, {
            icon: faTrash
        })
    ])
}

const toasted = useToasted();

const teamId = Spark.state.currentTeam.id;

const { getContext } = useCurrentGroup();

const autocompleteItems = ref([]);

const dataTypesLength = ref(false);

onMounted(async () => {
    await nextTick();
    if (isApiCall.value) {
        fetchDropdownData();
    }

    if (isExternalContactList.value && filter.value.value) {
        fetchSelectedExternalContactList();
    }
})

function selectDataType(dataType) {
    // DataTypeSelect can still emit its default data type while being unmounted
    // during an attribute switch, but external list filters must keep data_type null.
    if (isExternalContactList.value) {
        return;
    }

    filter.value.data_type = dataType;
    filter.value.operator = null;
    filter.value.value = null;

    if (isApiCall.value) {
        fetchDropdownData();
    }
}

function selectOperator(operator) {
    filter.value.operator = operator;

    if (!hasValue.value) {
        filter.value.value = null;
    }
}

function selectAttribute(attr) {
    filter.value.attribute_name = attr;

    // unsets prefilled data
    filter.value.data_type = null;
    filter.value.operator = null;
    filter.value.value = null;
    filter.value.key = null;

    if (!PROFILE_ATTRIBUTES.includes(attr)) {
        filter.value.profile = null;
    }

    autocompleteItems.value = [];
    selectedExternalContactList.value = null;
    selectedExternalContactListNotFound.value = false;

    if (isApiCall.value) {
        fetchDropdownData();
    }
}

const fetchDropdownData = debounce(async (query = null) => {
    const params = query ? { query } : null;

    try {
        let response;

        switch (filter.value.attribute_name) {
            case 'contacts.tags.tag_name':
            case 'contacts.tags.exclude_tags':
                response = await tagsClient.tagsAutocomplete(teamId, params)
                if (response.data.data.length > 0) {
                    autocompleteItems.value = response.data.data.map((e) => e.value.toString());
                }

                break;
            case 'contacts.custom_attributes.key':
            case 'contacts.custom_attributes.key_value':
            case 'contacts.custom_attributes.exclude_key_value':
            case 'contacts.custom_attributes.exclude':
                response = await customAttributesClient.customAttributesAutocomplete(teamId, filter.value.profile ? { ...params, profile: filter.value.profile } : params)
                if (response.data.data.length > 0) {
                    autocompleteItems.value = response.data.data;
                }

                break;
        }
    } catch (e) {
        console.error(e);

        toasted.global.platform_error();
    }
}, 300)

const hasValue = computed(() => {
    return !OPERATORS_THAT_DONT_NEED_VALUE.includes(filter.value.operator);
})

const shouldShowPositiveInt = computed(() => {
    return filter.value.data_type == 'date' && OPERATORS_N.includes(filter.value.operator);
})

const isCustomAttributeKeyValue = computed(() => {
    return filter.value.attribute_name === 'contacts.custom_attributes.key_value'
        || filter.value.attribute_name === 'contacts.custom_attributes.exclude_key_value';
})

const isExternalContactList = computed(() => {
    return filter.value.attribute_name === 'contacts.external_contact_lists.membership'
        || filter.value.attribute_name === 'contacts.external_contact_lists.exclude_membership';
})

const selectedExternalContactList = ref(null);
const selectedExternalContactListNotFound = ref(false);

// Persisted filters only store the list id, so the selected list is looked
// up to render it in the picker like a fresh selection. The lookup respects
// the current context's sharing scope, so a list no longer shared here
// resolves empty and renders as not found instead.
async function fetchSelectedExternalContactList() {
    try {
        const response = await externalListsClient.getExternalLists(getContext(), { filter: { id: filter.value.value } });

        selectedExternalContactList.value = response.data.data[0] ?? null;
        selectedExternalContactListNotFound.value = !selectedExternalContactList.value;
    } catch (e) {
        console.error(e);

        toasted.global.platform_error();
    }
}

function selectExternalContactList(externalContactList) {
    selectedExternalContactList.value = externalContactList ?? null;
    selectedExternalContactListNotFound.value = false;
    filter.value.key = null;
    filter.value.value = externalContactList ? externalContactList.id.toString() : null;
}

function selectProfile(profile) {
    filter.value.profile = profile === 'corporate' ? null : profile;

    autocompleteItems.value = [];

    if (isApiCall.value) {
        fetchDropdownData();
    }
}

const showProfile = computed(() => {
    return Boolean(FEATURES.local_attributes && PROFILE_ATTRIBUTES.includes(filter.value.attribute_name));
});

const isApiCall = computed(() => {
    const apiBackedAttributes = [
        'contacts.tags.tag_name',
        'contacts.custom_attributes.key',
        'contacts.custom_attributes.exclude',
        'contacts.tags.exclude_tags',
    ];

    return apiBackedAttributes.includes(filter.value.attribute_name) && (!filter.value.data_type || filter.value.data_type === 'text');
});

/**
 * @returns {{label: string, value: string}[]}
 */
const options = computed(() => {
    switch (filter.value.attribute_name) {
        case 'contacts.entry_point':
            return entryPointsForSelect();
        case 'contacts.phone_state':
            return phoneStateOptions;
        default:
            return [];
    }
})

const operatorCol = computed(() => {
    return dataTypesLength.value > 1 ? 'col-span-3' : 'col-span-6';
})

const canIgnoreYear = computed(() => {
    return Boolean(filter.value.data_type === 'date' &&
        OPERATORS_DATE.find(operator => operator.value === filter.value.operator?.slice(0, -14) || operator.value === filter.value.operator)?.ignoring_year);
});

const ignoringYear = computed(() => Boolean(filter.value.operator?.endsWith('_ignoring_year')));

const toggleIgnoringYear = (ignoresYear) => {
    if (ignoresYear) {
        filter.value.operator = `${filter.value.operator}_ignoring_year`;
    } else {
        filter.value.operator = filter.value.operator?.endsWith('_ignoring_year') ? filter.value.operator.slice(0, -14) : filter.value.operator;
    }
}
</script>
